import React, { useEffect, useState } from "react";
import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  sectionIcon: {
    fontSize: 28,
    color: theme.palette.primary.main,
    marginTop: 2,
  },
  messageCell: {
    maxWidth: 360,
    whiteSpace: "normal",
    wordBreak: "break-word",
    textAlign: "left",
    lineHeight: 1.5,
  },
  phoneCell: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
  },
  dateCell: {
    whiteSpace: "nowrap",
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    minWidth: 140,
  },
}));

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) {
    return value.substring(0, 16) || "—";
  }
  return date.toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const renderCampaign = (lead) => {
  if (lead.campaign_name) return lead.campaign_name;
  if (lead.utm_campaign) return lead.utm_campaign;
  return "—";
};

const renderStatus = (lead) => {
  if (lead.converted === 1) {
    const value = parseFloat(lead.sale_value || 0).toFixed(2);
    return (
      <Chip
        size="small"
        color="primary"
        label={i18n.t("leadMessages.status.converted", { value })}
      />
    );
  }

  return (
    <Chip
      size="small"
      style={{ backgroundColor: "#f59e0b", color: "#fff" }}
      label={i18n.t("leadMessages.status.pending")}
    />
  );
};

const LeadMessages = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lead-messages", {
        params: { limit: 50 },
      });
      setLeads(data.leads || []);
      setStats(data.stats || null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("leadMessages.title")}</Title>
        <MainHeaderButtonsWrapper>
          <IconButton onClick={fetchLeads} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.statsRow}>
        <div className={classes.statCard}>
          <Typography variant="caption">
            {i18n.t("leadMessages.stats.total")}
          </Typography>
          <Typography variant="h6">{stats?.total_leads ?? 0}</Typography>
        </div>
        <div className={classes.statCard}>
          <Typography variant="caption">
            {i18n.t("leadMessages.stats.today")}
          </Typography>
          <Typography variant="h6">{stats?.today_leads ?? 0}</Typography>
        </div>
        <div className={classes.statCard}>
          <Typography variant="caption">
            {i18n.t("leadMessages.stats.converted")}
          </Typography>
          <Typography variant="h6">{stats?.total_converted ?? 0}</Typography>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.sectionHeader}>
          <div className={classes.sectionTitleRow}>
            <ChatBubbleOutlineIcon className={classes.sectionIcon} />
            <div>
              <Typography variant="h6">
                {i18n.t("leadMessages.section.title")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("leadMessages.section.subtitle")}
              </Typography>
            </div>
          </div>
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">
                {i18n.t("leadMessages.table.date")}
              </TableCell>
              <TableCell align="left">
                {i18n.t("leadMessages.table.message")}
              </TableCell>
              <TableCell align="left">
                {i18n.t("leadMessages.table.destination")}
              </TableCell>
              <TableCell align="left">
                {i18n.t("leadMessages.table.campaign")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("leadMessages.table.status")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              leads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell align="left" className={classes.dateCell}>
                    {formatDateTime(lead.created_at)}
                  </TableCell>
                  <TableCell align="left" className={classes.messageCell}>
                    {lead.message || "—"}
                  </TableCell>
                  <TableCell align="left" className={classes.phoneCell}>
                    {lead.phone_wpp || "—"}
                  </TableCell>
                  <TableCell align="left">{renderCampaign(lead)}</TableCell>
                  <TableCell align="center">{renderStatus(lead)}</TableCell>
                </TableRow>
              ))}
            {!loading && leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("leadMessages.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default LeadMessages;
